// Pacote 21A (PR #46): tema light/dark via CSS vars.
//
// Estratégia:
//   - Default: dark (preserva visual atual). 1ª visita respeita
//     prefers-color-scheme do SO se for "light".
//   - Persist em localStorage com chave "novavida.theme".
//   - Aplica `.light` class no <html> root (dark é default sem class).
//
// Não usa Context — hook simples com listener cross-component via custom event.
// Mais leve, sem prop drilling, e aceita uso fora de provider.
import { useCallback, useEffect, useState } from "react"

export type Theme = "light" | "dark"

const STORAGE_KEY = "novavida.theme"
const EVENT_NAME = "novavida-theme-change"

function getInitialTheme(): Theme {
  // SSR safety
  if (typeof window === "undefined") return "dark"
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === "light" || stored === "dark") return stored
  // 1ª visita: respeita preferência do SO se for explicit light
  if (window.matchMedia?.("(prefers-color-scheme: light)").matches) return "light"
  return "dark"
}

function applyTheme(theme: Theme) {
  const root = document.documentElement
  if (theme === "light") {
    root.classList.add("light")
  } else {
    root.classList.remove("light")
  }
  // Hint pra browser ajustar scrollbar/form controls nativos
  root.style.colorScheme = theme
}

// Aplica imediatamente no boot (antes do React montar) pra evitar flash.
// Chamado em main.tsx via import side-effect.
export function bootstrapTheme(): void {
  if (typeof window === "undefined") return
  applyTheme(getInitialTheme())
}

export function useTheme(): {
  theme: Theme
  setTheme: (t: Theme) => void
  toggle: () => void
} {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme)

  // Listener pra outros componentes que mudam tema (sem reload)
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<{ theme: Theme }>).detail
      if (detail?.theme) setThemeState(detail.theme)
    }
    window.addEventListener(EVENT_NAME, onChange as EventListener)
    return () => window.removeEventListener(EVENT_NAME, onChange as EventListener)
  }, [])

  const setTheme = useCallback((t: Theme) => {
    localStorage.setItem(STORAGE_KEY, t)
    applyTheme(t)
    setThemeState(t)
    window.dispatchEvent(
      new CustomEvent(EVENT_NAME, { detail: { theme: t } })
    )
  }, [])

  const toggle = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark")
  }, [theme, setTheme])

  return { theme, setTheme, toggle }
}
